import { Inject, Injectable, Optional } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  bufferWhen,
  concatAll,
  filter,
  first,
  mergeMap,
  shareReplay,
  switchMapTo,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { filterBoolean, LocalStorageService } from '@recruitee/common';
import { FeatureFlagSelectors } from '@recruitee/feature-flags/data';

import { loadAnalytics } from './analytics-segment-loader';
import {
  AnalyticsConfig,
  AnalyticsEventDescriptor,
  AnalyticsProps,
  ANALYTICS_DISABLED,
  SegmentAPIMethods,
  SegmentClientOptions,
} from './analytics.types';

// eslint-disable-next-line no-console,no-restricted-syntax
const log = (type: string, args: any[]) => console.info(`🙈 analytics::${type}`, args);

const debugClient$ = of<SegmentAPIMethods>({
  alias(...args: any[]) {
    log(`alias`, args);
  },
  identify(...args: any[]) {
    const callback = args.slice(-1).pop();
    log(`identify`, args);
    if (typeof callback === 'function') {
      callback();
    }
  },
  page(...args: any[]) {
    log(`page`, args);
  },
  track(...args: any[]) {
    log(`track`, args);
  },
  reset() {
    log(`reset`, []);
  },
});

@Injectable({ providedIn: 'root' })
export class AnalyticsSegmentClient implements SegmentAPIMethods {
  public flushBuffer = new Subject<void>();
  public emitter$ = new Subject<AnalyticsEventDescriptor>();

  public buffered$ = this.emitter$.pipe(
    bufferWhen(() => this.flushBuffer.asObservable()),
    first(),
    mergeMap(values =>
      of(
        ...values.sort(a => {
          if (a.method === 'identify') {
            return -1;
          }

          return 0;
        }),
      ),
    ),
  );

  private events$ = of(this.buffered$, this.emitter$).pipe(concatAll());
  private identify$ = this.emitter$.pipe(
    filter(evt => evt.method === 'identify'),
    first(),
    shareReplay(1),
  );

  constructor(
    private config: AnalyticsConfig,
    @Optional() private ls: LocalStorageService,
    @Optional() private store?: Store,
    @Optional() @Inject(ANALYTICS_DISABLED) analyticsDisabled?: boolean,
  ) {
    const enabled = analyticsDisabled !== true && (config.isAvailable || config.isDebug);

    if (enabled) {
      this.identify$.subscribe();

      if (config.requiredFeatureFlag && store) {
        store
          .select(FeatureFlagSelectors.isLoaded)
          .pipe(
            filterBoolean,
            switchMapTo(
              store.select(FeatureFlagSelectors.featureEnabled(config.requiredFeatureFlag)),
            ),
            first(),
            tap(flag => {
              if (flag) {
                this.install(config);
              } else {
                config.opts.debug = true;
                config.opts.waitForIdentify = false;
                this.install(config);
              }
            }),
          )
          .subscribe();
      } else {
        this.install(config);
      }
    }
  }

  public reset() {
    this.emitter$.next({
      data: {},
      options: {},
      method: 'reset',
    });
  }

  public track(name: string, props?: AnalyticsProps, options?: SegmentClientOptions) {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'track',
      data: {
        name,
        props,
      },
    });
  }

  public alias(id: number, options?: SegmentClientOptions): void {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'alias',
      data: {
        id,
      },
    });
  }

  public identify(
    userId?: number,
    traits?: Record<string, string | number>,
    options?: SegmentClientOptions,
  ): void {
    this.emitter$.next({
      options: this.getConfig(options),
      method: 'identify',
      data: {
        userId,
        traits,
      },
    });
  }

  public page(
    category: string,
    name: string,
    properties?: any,
    options?: SegmentClientOptions,
  ): void {
    this.emitter$.next({
      method: 'page',
      data: {
        category,
        name,
        properties: this.config.pageTrackParamsSanitizer(name, category, properties),
      },
      options: this.getConfig(options),
    });
  }

  private install(config: AnalyticsConfig) {
    const client$ = (
      !config.isDebug ? loadAnalytics(config.key, config.options) : debugClient$
    ).pipe(shareReplay(1));

    config.waitForIdentify
      ? this.runWithIdentifyRequired(client$)
      : this.runWithoutIdentifyRequired(client$);
  }

  private runWithIdentifyRequired(client$: Observable<SegmentAPIMethods>) {
    this.attachClient(client$);

    combineLatest([this.identify$, client$])
      .pipe(first())
      .subscribe(([evt, client]) => {
        client.identify(evt.data.userId, evt.data.traits, evt.options, () =>
          this.flushBuffer.next(),
        );
      });
  }

  private runWithoutIdentifyRequired(client$: Observable<SegmentAPIMethods>) {
    this.attachClient(client$);
    client$.subscribe(() => this.flushBuffer.next());
  }

  private attachClient(client$: Observable<SegmentAPIMethods>) {
    this.events$.pipe(withLatestFrom(client$)).subscribe({
      next: ([evt, client]) => {
        // eslint-disable-next-line default-case
        switch (evt.method) {
          case 'reset':
            client.reset();
            break;
          case 'track':
            client.track(evt.data.name, evt.data.props || {}, evt.options);
            break;
          case 'identify':
            client.identify(evt.data.userId, evt.data.traits, evt.options);
            break;
          case 'alias':
            client.alias(evt.data.id);
            break;
          case 'page':
            client.page(evt.data.category, evt.data.name, evt.data.properties, evt.options);
            break;
        }
      },
    });
  }

  private getConfig(options: SegmentClientOptions = {}): SegmentClientOptions {
    return {
      ...this.config.options,
      integrations: <SegmentClientOptions['integrations']>{
        ...this.config.options.integrations,
        ...(options?.integrations || {}),
      },
    };
  }
}
